import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Form, Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import {
    getQuestionSet_user as getQuestionSet,
    updateQuestion_user as updateQuestion,
    addQuestion_user as addQuestion,
    deleteQuestion_user as deleteQuestion
} from './../api';

const Questions = () => {
    const { id } = useParams();
    const [questionSet, setQuestionSet] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [newQuestion, setNewQuestion] = useState(false);
    const [alert, setAlert] = useState(null);
    const [alertTimeout, setAlertTimeout] = useState(null);
    const [tempText, setTempText] = useState('');
    const navigate = useNavigate();

    const setTimedAlert = (alertData) => {
        if (alertTimeout) {
            clearTimeout(alertTimeout);
        }
        setAlert(alertData);
        const timeout = setTimeout(() => {
            setAlert(null);
        }, 2000);
        setAlertTimeout(timeout);
    };

    useEffect(() => {
        return () => {
            if (alertTimeout) {
                clearTimeout(alertTimeout);
            }
        };
    }, [alertTimeout]);

    const fetchQuestionSet = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getQuestionSet(id);
            const sortedQuestions = response.data.questions.sort((a, b) =>
                (a.order || 0) - (b.order || 0)
            );
            setQuestionSet({ ...response.data, questions: sortedQuestions });
        } catch (error) {
            console.error('Error fetching question set:', error);
            setTimedAlert({ variant: 'danger', message: 'Failed to fetch questions' });
        }
        setIsLoading(false);
    }, [id]);

    useEffect(() => {
        fetchQuestionSet();
    }, [fetchQuestionSet]);

    const handleSave = async (questionId, text) => {
        try {
            if (questionId) {
                await updateQuestion(id, questionId, { text });
            } else {
                const lastOrder = Math.max(...questionSet.questions.map(q => q.order || 0), 0);
                await addQuestion(id, {
                    text,
                    order: lastOrder + 1
                });
            }
            await fetchQuestionSet();
            setTimedAlert({ variant: 'success', message: questionId ? 'Question updated successfully' : 'Question added successfully' });
            setEditingId(null);
            setNewQuestion(false);
            setTempText('');
        } catch (error) {
            console.error('Error saving question:', error);
            setTimedAlert({ variant: 'danger', message: 'Failed to save question' });
        }
    };

    const handleMoveQuestion = async (questionId, direction) => {
        try {
            const questions = [...questionSet.questions];
            const currentIndex = questions.findIndex(q => q.id === questionId);
            const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

            if (newIndex < 0 || newIndex >= questions.length) return;

            const currentQuestion = questions[currentIndex];
            const targetQuestion = questions[newIndex];

            questions[currentIndex] = targetQuestion;
            questions[newIndex] = currentQuestion;

            setQuestionSet(prevSet => ({
                ...prevSet,
                questions: questions
            }));

            try {
                await updateQuestion(id, questionId, {
                    ...currentQuestion,
                    order: targetQuestion.order
                });

                await updateQuestion(id, targetQuestion.id, {
                    ...targetQuestion,
                    order: currentQuestion.order
                });

                await fetchQuestionSet();
                setTimedAlert({ variant: 'success', message: 'Question order updated successfully' });
            } catch (error) {
                console.error('Error updating question order:', error);
                setTimedAlert({ variant: 'danger', message: 'Failed to update question order' });
                fetchQuestionSet();
            }
        } catch (error) {
            console.error('Error updating question order:', error);
            setTimedAlert({ variant: 'danger', message: 'Failed to update question order' });
        }
    };

    const handleDelete = async (questionId) => {
        if (window.confirm('Are you sure you want to delete this question?')) {
            try {
                await deleteQuestion(id, questionId);
                const remainingQuestions = questionSet.questions
                    .filter(q => q.id !== questionId)
                    .map((q, index) => ({
                        ...q,
                        order: index + 1
                    }));

                await Promise.all(remainingQuestions.map(q =>
                    updateQuestion(id, q.id, { order: q.order })
                ));

                await fetchQuestionSet();
                setTimedAlert({ variant: 'success', message: 'Question deleted successfully' });
            } catch (error) {
                console.error('Error deleting question:', error);
                setTimedAlert({ variant: 'danger', message: 'Failed to delete question' });
            }
        }
    };

    if (isLoading) {
        return <div className="text-center mt-5"><Spinner animation="border" /></div>;
    }

    return (
        <Container fluid className="d-flex flex-column vh-100">
            <Row className="justify-content-center">
                <Col xs={12} lg={10} xl={8}>
                    <h1 className="my-4 text-center">{questionSet?.category} - Questions</h1>
                    {alert && (
                        <Alert variant={alert.variant} onClose={() => setAlert(null)} dismissible>
                            {alert.message}
                        </Alert>
                    )}
                </Col>
            </Row>

            <Row className="justify-content-center flex-grow-1">
                <Col xs={12} lg={10} xl={8}>
                    <div className="border rounded p-4 mb-4">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th style={{ width: '60px' }}>#</th>
                                    <th>Question</th>
                                    <th style={{ width: '180px' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {questionSet?.questions.map((question, index) => (
                                    <tr key={question.id}>
                                        <td>{index + 1}</td>
                                        <td>
                                            {editingId === question.id ? (
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    value={tempText}
                                                    onChange={(e) => setTempText(e.target.value)}
                                                    autoFocus
                                                />
                                            ) : (
                                                question.text
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                {editingId === question.id ? (
                                                    <>
                                                        <Button
                                                            variant="outline-success"
                                                            size="sm"
                                                            onClick={() => handleSave(question.id, tempText)}
                                                        >
                                                            <i className="bi bi-check"></i>
                                                        </Button>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => {
                                                                setEditingId(null);
                                                                setTempText('');
                                                            }}
                                                        >
                                                            <i className="bi bi-x"></i>
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleMoveQuestion(question.id, 'up')}
                                                            disabled={index === 0}
                                                        >
                                                            <i className="bi bi-arrow-up"></i>
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleMoveQuestion(question.id, 'down')}
                                                            disabled={index === questionSet.questions.length - 1}
                                                        >
                                                            <i className="bi bi-arrow-down"></i>
                                                        </Button>
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => {
                                                                setEditingId(question.id);
                                                                setTempText(question.text);
                                                            }}
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(question.id)}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {newQuestion && (
                                    <tr>
                                        <td>{(questionSet?.questions.length || 0) + 1}</td>
                                        <td>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={tempText}
                                                onChange={(e) => setTempText(e.target.value)}
                                                autoFocus
                                            />
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    onClick={() => handleSave(null, tempText)}
                                                >
                                                    <i className="bi bi-check"></i>
                                                </Button>
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    onClick={() => {
                                                        setNewQuestion(false);
                                                        setTempText('');
                                                    }}
                                                >
                                                    <i className="bi bi-x"></i>
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>

                        <div className="mt-3">
                            {!newQuestion && (
                                <Button
                                    variant="primary"
                                    onClick={() => {
                                        setNewQuestion(true);
                                        setTempText('');
                                        setEditingId(null);
                                    }}
                                >
                                    Add Input Field
                                </Button>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default Questions;